import React, { useEffect, useMemo, useState } from 'react';
import { ShoppingCart, Users, LineChart, Globe } from 'lucide-react';
import { isDemoBrand } from '../../utils/demoBrand';

// Google Product Taxonomy — top-level categories for home/furniture/textile brands
const HOME_FURNITURE_CATEGORIES = [
  { id: '436', label: '436 — Mobilyalar (tümü)' },
  { id: '6433', label: '6433 — Yataklar ve Aksesuarlar' },
  { id: '460', label: '460 — Kanepeler' },
  { id: '6356', label: '6356 — Dolaplar ve Saklama' },
  { id: '4171', label: '4171 — Ev Tekstili (tümü)' },
  { id: '569', label: '569 — Yatak Takımları (Bedding)' },
  { id: '4077', label: '4077 — Havlular' },
  { id: '598', label: '598 — Kilimler' },
  { id: '2882', label: '2882 — Perdeler' },
];

// Brands that should default to home/furniture categories
const HOME_FURNITURE_BRAND_IDS = new Set([
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e', // EnzaHome
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7', // Yataş Bedding
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f', // Puffy
]);

// Brand-specific default category overrides
const BRAND_DEFAULT_CATEGORY = {
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e': '436', // EnzaHome → Mobilyalar
};

export default function MerchantInsights2() {
  // Live data states
  const [bestSellers, setBestSellers] = useState([]);
  const [bestSellersLoading, setBestSellersLoading] = useState(false);
  const [bestSellersCategoryOptionsLoading, setBestSellersCategoryOptionsLoading] = useState(false);
  const [bestSellersCategoryOptions, setBestSellersCategoryOptions] = useState([]); // [{id,label}]
  const [categoryOptionsFetched, setCategoryOptionsFetched] = useState(false);

  const currentBrandId = (
    localStorage.getItem('selectedBrandId') ||
    localStorage.getItem('currentBrandId') ||
    sessionStorage.getItem('selectedBrandId') ||
    ''
  );
  const isHomeFurnitureBrand = HOME_FURNITURE_BRAND_IDS.has(currentBrandId);

  const [bestSellersCategoryId, setBestSellersCategoryId] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const fromUrl = sp.get('bsCategoryId') || sp.get('categoryId');
      if (fromUrl) return String(fromUrl).trim();
      // Default to brand-specific category, or Beds & Accessories for home/furniture brands
      const bid = localStorage.getItem('selectedBrandId') || localStorage.getItem('currentBrandId') || '';
      if (BRAND_DEFAULT_CATEGORY[bid]) return BRAND_DEFAULT_CATEGORY[bid];
      if (HOME_FURNITURE_BRAND_IDS.has(bid)) return '6433';
      return '166';
    } catch (_) {
      return '166';
    }
  });
  const [cvCompetitors, setCvCompetitors] = useState([]);
  const [cvTopMerchants, setCvTopMerchants] = useState([]);
  const [topicTrends, setTopicTrends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [cvLoading, setCvLoading] = useState(false);
  const [cvError, setCvError] = useState('');
  const BEST_SELLERS_PAGE_SIZE = 25;
  const [bestSellersPage, setBestSellersPage] = useState(1);
  const [cvCategoryId, setCvCategoryId] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const fromUrl = sp.get('cvCategoryId') || sp.get('categoryId');
      if (fromUrl) return String(fromUrl).trim();
      const bid = localStorage.getItem('selectedBrandId') || localStorage.getItem('currentBrandId') || '';
      if (BRAND_DEFAULT_CATEGORY[bid]) return BRAND_DEFAULT_CATEGORY[bid];
      if (HOME_FURNITURE_BRAND_IDS.has(bid)) return '6433';
      return '166';
    } catch (_) {
      return '166';
    }
  });

  const isAbortErr = (e) => {
    if (!e) return false;
    const name = e.name || '';
    const msg = e.message || String(e) || '';
    return name === 'AbortError' || msg.includes('AbortError') || msg.includes('signal is aborted');
  };

  const fetchSafe = async (url, options) => {
    try {
      const r = await fetch(url, options);
      return r;
    } catch (e) {
      if (isAbortErr(e)) return null;
      throw e;
    }
  };

  // Helper to get brand id from storage
  const getBrandId = () => {
    return (
      localStorage.getItem('selectedBrandId') ||
      localStorage.getItem('currentBrandId') ||
      sessionStorage.getItem('selectedBrandId') ||
      ''
    );
  };

  const getQueryParam = (key, fallback = '') => {
    try {
      const v = new URLSearchParams(window.location.search).get(key);
      return (v == null || String(v).trim() === '') ? fallback : String(v);
    } catch (_) {
      return fallback;
    }
  };

  const normalizePct01 = (raw) => {
    const v = Number(raw ?? 0);
    if (!Number.isFinite(v)) return 0;
    // Some MC fields are 0-100, some are already 0-1
    return v > 1 ? v / 100 : v;
  };

  // Fetch from backend read endpoints
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        if (alive) setLoading(true);
        if (alive) setError('');

        const brandId = getBrandId();
        const headers = {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        };

        // Resolve merchantId from existing MERCHANT_CENTER connection
        let resolvedMerchantId = '';
        try {
          const c = await fetchSafe('https://api.glowytics.com/api/auth/connections', {
            method: 'GET',
            headers,
            credentials: 'include'
          });
          if (c && c.ok) {
            const connections = await c.json();
            const arr = Array.isArray(connections) ? connections : [];
            const found = arr.find(x => String(x?.platform || '').toUpperCase() === 'MERCHANT_CENTER') || null;
            resolvedMerchantId = String(found?.customerId || found?.merchantId || found?.accountId || found?.merchant_id || '').trim();
          }
        } catch (_) {}

        if (alive) setMerchantId(resolvedMerchantId);

        if (!resolvedMerchantId && !isDemoBrand(brandId)) {
          if (alive) setError('Merchant Center account not selected. Go to Settings → Merchant Center and choose an account.');
          return;
        }

        // Date range: last 30 days by default (override via URL params)
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 30);
        const toISO = (d) => d.toISOString().split('T')[0];
        const startDate = getQueryParam('startDate', toISO(start));
        const endDate = getQueryParam('endDate', toISO(end));
        const country = getQueryParam('country', 'TR').trim().toUpperCase();

        // DEMO MODE: fill all sections with dummy data for specific brand(s)
        if (isDemoBrand(brandId)) {
          const demoBest = Array.from({ length: 20 }, (_, i) => ({
            rank: i + 1,
            title: `Demo Bestseller ${String.fromCharCode(65 + (i % 26))}`,
            brand: ['Alpha', 'Beta', 'Gamma', 'Delta', 'Omega'][i % 5],
            category: [
              'Ev Dekorasyonu > Halı & Kilim',
              'Elektronik > Kulaklık',
              'Moda > Ayakkabı',
              'Spor > Aksesuar',
              'Mutfak > Küçük Ev Aletleri'
            ][i % 5],
            relativeDemand: ['HIGH', 'MEDIUM', 'LOW', 'MEDIUM', 'HIGH'][i % 5],
          }));
          const demoComp = Array.from({ length: 12 }, (_, i) => ({
            rank: i + 1,
            domain: i === 2 ? 'yourbrand.com' : `competitor${i + 1}.com`,
            relativeVisibility: Math.max(0.05, 0.6 - i * 0.035),
            higherPositionRate: Math.max(0.1, 0.5 - i * 0.03),
            pageOverlapRate: Math.max(0.05, 0.45 - i * 0.028),
            isYourDomain: i === 2,
          }));
          const demoTopMerchants = Array.from({ length: 12 }, (_, i) => ({
            rank: i + 1,
            domain: `merchant${i + 1}.com`,
            pageOverlapRate: Math.max(0.05, 0.55 - i * 0.03),
            adsOrganicRatio: 0.3 + (i % 5) * 0.08,
            isYourDomain: i === 4,
          }));
          const topics = [
            'Kamp Çadırı',
            'Koşu Ayakkabısı',
            'Robot Süpürge',
            'Espresso Makinesi',
            'Bebek Bezi',
            'Akıllı Saat',
            'Bluetooth Kulaklık',
            'Protein Tozu',
            'Makyaj Seti',
            'Bisiklet',
            'Notebook',
            'Spor Ayakkabı'
          ];
          const demoTrends = topics.map((t, idx) => {
            const base = 0.3 + (idx % 6) * 0.08;
            return {
              customerCountryCode: 'TR',
              topic: t,
              searchInterest: Math.min(0.95, base),
              last7: Math.min(0.95, base + 0.02 * ((idx % 3) - 1)),
              last30: Math.min(0.95, base + 0.04 * ((idx % 4) - 1.5)),
              last90: Math.min(0.95, base + 0.06 * ((idx % 5) - 2)),
              last120: Math.min(0.95, base + 0.08 * ((idx % 6) - 3)),
            };
          });
          if (alive) {
            setBestSellers(demoBest);
            setCvCompetitors(demoComp);
            setCvTopMerchants(demoTopMerchants);
            setTopicTrends(demoTrends.slice(0, 25));
            setLoading(false);
          }
          return;
        }

        // Topic Trends
        try {
          const r = await fetchSafe(
            `https://api.glowytics.com/api/auth/merchant-center/reports/topic-trends?merchantId=${encodeURIComponent(resolvedMerchantId)}&country=${encodeURIComponent(country)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&pageSize=1000`,
            { method: 'GET', headers, credentials: 'include' }
          );
          if (r && r.ok) {
            const j = await r.json();
            const rows = Array.isArray(j?.items) ? j.items : [];
            if (rows.length && alive) {
              const mapped = rows.map((x) => ({
                customerCountryCode: x.customerCountryCode || x.customer_country_code || country || 'TR',
                topic: x.topic || '',
                searchInterest: normalizePct01(x.searchInterest ?? x.search_interest),
                last7: normalizePct01(x.last7DaysSearchInterest ?? x.last7_days_search_interest),
                last30: normalizePct01(x.last30DaysSearchInterest ?? x.last30_days_search_interest),
                last90: normalizePct01(x.last90DaysSearchInterest ?? x.last90_days_search_interest),
                last120: normalizePct01(x.last120DaysSearchInterest ?? x.last120_days_search_interest),
              }));
              if (alive) setTopicTrends(mapped.slice(0, 25));
            }
          }
        } catch (_e) { /* ignore to accumulate other sections */ }

        // CV + Top Merchants fetched in a separate effect so category changes can refetch them

      } catch (e) {
        if (isAbortErr(e)) {
          // ignore aborts
        } else {
          if (alive) setError('Failed to load Merchant Insights data.');
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, []);

  const [cvCategoryManuallySet, setCvCategoryManuallySet] = useState(false);

  useEffect(() => {
    if (!cvCategoryManuallySet && bestSellersCategoryId) setCvCategoryId(bestSellersCategoryId);
  }, [bestSellersCategoryId, cvCategoryManuallySet]);

  // Competitive Visibility + Top Merchants (refetch when cvCategoryId changes)
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const brandId = getBrandId();
        if (isDemoBrand(brandId)) return;
        if (!merchantId) return;
        if (!cvCategoryId) return;

        if (alive) {
          setCvLoading(true);
          setCvError('');
        }

        const headers = {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        };
        const country = getQueryParam('country', 'TR').trim().toUpperCase();
        const trafficSource = getQueryParam('trafficSource', 'ADS').trim().toUpperCase(); // ADS|ORGANIC|ALL
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 30);
        const toISO = (d) => d.toISOString().split('T')[0];
        const startDate = getQueryParam('startDate', toISO(start));
        const endDate = getQueryParam('endDate', toISO(end));

        // CV Competitors (paged)
        {
          const base = new URLSearchParams();
          base.set('merchantId', merchantId);
          base.set('country', country);
          base.set('categoryId', cvCategoryId);
          base.set('trafficSource', trafficSource);
          base.set('startDate', startDate);
          base.set('endDate', endDate);
          base.set('pageSize', '1000');

          let pageToken = '';
          let guard = 0;
          let allRows = [];
          while (guard < 20) {
            const qs = new URLSearchParams(base);
            if (pageToken) qs.set('pageToken', pageToken);
            const r = await fetchSafe(
              `https://api.glowytics.com/api/auth/merchant-center/reports/competitive-visibility/competitors?${qs.toString()}`,
              { method: 'GET', headers, credentials: 'include' }
            );
            if (!r) break;
            const txt = await r.text();
            let j; try { j = JSON.parse(txt); } catch { j = null; }
            if (!r.ok) throw new Error(j?.error || `CV competitors failed (${r.status})`);
            const rows = Array.isArray(j?.items) ? j.items : [];
            allRows = allRows.concat(rows);
            pageToken = String(j?.nextPageToken || '');
            if (!pageToken) break;
            guard += 1;
          }
          if (alive) {
            const mapped = allRows.map((x, idx) => ({
              rank: Number(x.rank || idx + 1),
              domain: x.domain || '',
              relativeVisibility: Number(x.relativeVisibility ?? x.relative_visibility ?? 0),
              higherPositionRate: Number(x.higherPositionRate ?? x.higher_position_rate ?? 0),
              pageOverlapRate: Number(x.pageOverlapRate ?? x.page_overlap_rate ?? 0),
              isYourDomain: Boolean(x.isYourDomain ?? x.is_your_domain ?? false)
            }));
            setCvCompetitors(mapped);
          }
        }

        // Top Merchants
        {
          const qs = new URLSearchParams();
          qs.set('merchantId', merchantId);
          qs.set('country', country);
          qs.set('categoryId', cvCategoryId);
          qs.set('trafficSource', trafficSource);
          qs.set('startDate', startDate);
          qs.set('endDate', endDate);
          qs.set('pageSize', '1000');

          const r = await fetchSafe(
            `https://api.glowytics.com/api/auth/merchant-center/reports/competitive-visibility/top-merchants?${qs.toString()}`,
            { method: 'GET', headers, credentials: 'include' }
          );
          if (r) {
            const txt = await r.text();
            let j; try { j = JSON.parse(txt); } catch { j = null; }
            if (!r.ok) throw new Error(j?.error || `Top merchants failed (${r.status})`);
            const rows = Array.isArray(j?.items) ? j.items : [];
            if (alive) {
              const mapped = rows.map((x, idx) => ({
                rank: Number(x.rank || idx + 1),
                domain: x.domain || '',
                pageOverlapRate: Number(x.pageOverlapRate ?? x.page_overlap_rate ?? 0),
                adsOrganicRatio: Number(x.adsOrganicRatio ?? x.ads_organic_ratio ?? 0),
                isYourDomain: Boolean(x.isYourDomain ?? x.is_your_domain ?? false)
              }));
              setCvTopMerchants(mapped);
            }
          }
        }
      } catch (e) {
        if (!alive) return;
        setCvError(e?.message || 'Failed to load Competitive Visibility');
      } finally {
        if (alive) setCvLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [merchantId, cvCategoryId]);

  // Best Sellers categories (fetch once, unfiltered; then user can filter)
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const brandId = getBrandId();
        if (isDemoBrand(brandId)) return;
        if (!merchantId) return;
        if (categoryOptionsFetched) return;

        if (alive) setBestSellersCategoryOptionsLoading(true);

        const headers = {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        };
        const country = getQueryParam('country', 'TR').trim().toUpperCase();
        const bestSellersGranularity = getQueryParam('granularity', 'WEEKLY').trim().toUpperCase(); // WEEKLY|MONTHLY
        const bestSellersReportDate = getQueryParam('reportDate', '').trim(); // optional

        const qs = new URLSearchParams();
        qs.set('merchantId', merchantId);
        if (country) qs.set('country', country);
        if (bestSellersGranularity) qs.set('granularity', bestSellersGranularity);
        if (bestSellersReportDate) qs.set('reportDate', bestSellersReportDate);
        // IMPORTANT: do NOT set categoryId here -> we want to discover all categories available
        qs.set('pageSize', '1000');

        let pageToken = '';
        let guard = 0;
        let rows = [];
        while (guard < 20) {
          const qsp = new URLSearchParams(qs);
          if (pageToken) qsp.set('pageToken', pageToken);
          const r = await fetchSafe(
            `https://api.glowytics.com/api/auth/merchant-center/reports/best-sellers?${qsp.toString()}`,
            { method: 'GET', headers, credentials: 'include' }
          );
          if (!r || !r.ok) break;
          const j = await r.json();
          const items = Array.isArray(j?.items) ? j.items : [];
          rows = rows.concat(items);
          pageToken = String(j?.nextPageToken || '');
          if (!pageToken) break;
          guard += 1;
        }
        if (!alive) return;

        const labelById = new Map();
        for (const x of rows) {
          const id = String(x?.categoryId ?? x?.category_id ?? '').trim();
          if (!id) continue;
          if (labelById.has(id)) continue;
          const cat = [x?.categoryL1, x?.categoryL2, x?.categoryL3].filter(Boolean).join(' > ');
          labelById.set(id, cat ? `${id} — ${cat}` : id);
        }
        // Merge API-discovered categories with curated home/furniture list
        const apiOpts = Array.from(labelById.entries()).map(([id, label]) => ({ id, label }));
        // Always include curated categories (they may not appear in best-sellers API results)
        const merged = new Map();
        for (const o of HOME_FURNITURE_CATEGORIES) merged.set(o.id, o);
        for (const o of apiOpts) merged.set(o.id, o); // API labels override if same id
        const opts = Array.from(merged.values()).sort((a, b) => Number(a.id) - Number(b.id));

        if (alive) {
          setBestSellersCategoryOptions(opts);
          setCategoryOptionsFetched(true);
        }
      } catch (_) {
        // ignore
      } finally {
        if (alive) setBestSellersCategoryOptionsLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [merchantId, categoryOptionsFetched]);

  // Best Sellers (refetch on category filter)
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const brandId = getBrandId();
        if (isDemoBrand(brandId)) return;
        if (!merchantId) return;

        if (alive) setBestSellersLoading(true);

        const headers = {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        };
        const country = getQueryParam('country', 'TR').trim().toUpperCase();
        const bestSellersGranularity = getQueryParam('granularity', 'WEEKLY').trim().toUpperCase(); // WEEKLY|MONTHLY
        const bestSellersReportDate = getQueryParam('reportDate', '').trim(); // optional

        const qs = new URLSearchParams();
        qs.set('merchantId', merchantId);
        if (country) qs.set('country', country);
        if (bestSellersGranularity) qs.set('granularity', bestSellersGranularity);
        if (bestSellersReportDate) qs.set('reportDate', bestSellersReportDate);
        if (bestSellersCategoryId) qs.set('categoryId', bestSellersCategoryId);
        qs.set('pageSize', '1000');

        const r = await fetchSafe(
          `https://api.glowytics.com/api/auth/merchant-center/reports/best-sellers?${qs.toString()}`,
          { method: 'GET', headers, credentials: 'include' }
        );
        if (!r || !r.ok) return;
        const j = await r.json();
        const rows = Array.isArray(j?.items) ? j.items : [];
        if (!alive) return;

        const mapped = rows.map((x, idx) => ({
          rank: Number(x.rank || idx + 1),
          title: x.title || '',
          brand: x.brand || '',
          category: [x.categoryL1, x.categoryL2, x.categoryL3].filter(Boolean).join(' > ') || '',
          relativeDemand: x.relativeDemand || x.relative_demand || 'MEDIUM'
        }));
        setBestSellers(mapped);
      } catch (_) {
        // ignore
      } finally {
        if (alive) setBestSellersLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [bestSellersCategoryId, merchantId]);

  useEffect(() => {
    // Reset pagination on filter/data changes
    setBestSellersPage(1);
  }, [bestSellersCategoryId]);

  const bestSellersPageCount = useMemo(() => {
    return Math.max(1, Math.ceil((bestSellers?.length || 0) / BEST_SELLERS_PAGE_SIZE));
  }, [bestSellers?.length]);

  const pagedBestSellers = useMemo(() => {
    const page = Math.min(Math.max(1, bestSellersPage), bestSellersPageCount);
    const start = (page - 1) * BEST_SELLERS_PAGE_SIZE;
    const end = start + BEST_SELLERS_PAGE_SIZE;
    return (bestSellers || []).slice(start, end);
  }, [bestSellers, bestSellersPage, bestSellersPageCount]);

  const bestSellersPageButtons = useMemo(() => {
    const total = bestSellersPageCount;
    const current = Math.min(Math.max(1, bestSellersPage), total);
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const out = new Set([1, total, current, current - 1, current - 2, current + 1, current + 2]);
    const nums = Array.from(out).filter(n => n >= 1 && n <= total).sort((a, b) => a - b);
    return nums;
  }, [bestSellersPage, bestSellersPageCount]);

  // KPIs (kept for parity; UI cards removed below as in original)
  const kpi = useMemo(() => {
    const categories = new Set(bestSellers.map(x => x.category));
    const youRank = cvCompetitors.find(x => x.isYourDomain)?.rank || null;
    const avgRelVis = (cvCompetitors.reduce((s, x) => s + x.relativeVisibility, 0) / cvCompetitors.length) || 0;
    return {
      bestSellerCount: bestSellers.length,
      categoriesCount: categories.size,
      competitorsCount: cvCompetitors.length,
      yourRank: youRank,
      avgVisibility: avgRelVis,
      topicsCount: topicTrends.length,
    };
  }, [bestSellers, cvCompetitors, topicTrends]);
  void kpi;

  // More explicit visualization: top competitors by relative visibility (bars)
  const RelVisBars = ({ data, limit = Infinity }) => {
    try {
      const items = [...data]
        .sort((a, b) => b.relativeVisibility - a.relativeVisibility)
        .slice(0, limit);
      const max = Math.max(0.001, ...items.map(i => i.relativeVisibility));
      return (
        <div className="min-w-[280px]">
          <div className="text-[11px] text-gray-500 mb-2">
            Relative visibility = share of overlapping impressions you have vs each domain.
          </div>
          <div className="space-y-2">
            {items.map((it, idx) => {
              const pct = Math.max(0, Math.min(1, it.relativeVisibility));
              const isYou = !!it.isYourDomain;
              return (
                <div key={`${it.domain}-${idx}`}>
                  <div className="flex justify-between text-[11px] text-gray-600 mb-1">
                    <span className={`${isYou ? 'font-medium text-indigo-700' : ''}`}>{it.domain}{isYou ? ' (you)' : ''}</span>
                    <span className={`${isYou ? 'font-semibold text-indigo-700' : 'text-gray-700'}`}>{(pct * 100).toFixed(1)}%</span>
                  </div>
                  <div className="h-2.5 w-full bg-gray-100 rounded">
                    <div
                      className={`h-2.5 rounded ${isYou ? 'bg-indigo-500' : 'bg-gray-300'}`}
                      style={{ width: `${(pct / max) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 text-[11px] text-gray-500">
            Tip: Higher position rate and overlap help explain why a domain wins visibility.
          </div>
        </div>
      );
    } catch {
      return null;
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {loading && (
          <div className="mb-4 text-xs px-3 py-2 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
            Loading Merchant Insights...
          </div>
        )}
        {!!error && !loading && (
          <div className="mb-4 text-xs px-3 py-2 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
            {error}
          </div>
        )}
        {!!cvError && !loading && (
          <div className="mb-4 text-xs px-3 py-2 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
            {cvError}
          </div>
        )}
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <img src="/google-merchant-centerpngg.svg" alt="Merchant Center" className="h-6 w-auto" />
            <h1 className="text-xl font-semibold text-gray-900">Merchant Insights</h1>
          </div>
          <div className="text-sm text-gray-500">Top Merchants, Competitive Visibility, Best Sellers, Topic Trends</div>
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top merchants */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <Users className="w-4 h-4 text-indigo-600" />
                <span>Top Merchants</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-[11px] text-gray-500 whitespace-nowrap">Category</div>
                <select
                  value={cvCategoryId}
                  onChange={(e) => { setCvCategoryId(String(e.target.value || '').trim()); setCvCategoryManuallySet(true); }}
                  className="px-2 py-1 text-[11px] border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 max-w-[360px] w-full sm:w-auto"
                  title="Top Merchants / CV categoryId"
                >
                  {bestSellersCategoryOptionsLoading && !bestSellersCategoryOptions.length ? (
                    <option value={cvCategoryId || '166'}>Loading...</option>
                  ) : bestSellersCategoryOptions.length ? (
                    bestSellersCategoryOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))
                  ) : (
                    <option value={cvCategoryId || '166'}>{cvCategoryId || '166'}</option>
                  )}
                </select>
                {cvLoading && <span className="text-[11px] text-gray-400">loading...</span>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-2 py-1">rank</th>
                    <th className="px-2 py-1">domain</th>
                    <th className="px-2 py-1">overlap</th>
                    <th className="px-2 py-1">ads/organic</th>
                  </tr>
                </thead>
                <tbody>
                  {cvTopMerchants.map((t, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1 text-right">{t.rank}</td>
                      <td className="px-2 py-1">{t.domain}{t.isYourDomain ? ' (you)' : ''}</td>
                      <td className="px-2 py-1 text-right">{(t.pageOverlapRate * 100).toFixed(1)}%</td>
                      <td className="px-2 py-1 text-right">{(t.adsOrganicRatio * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Competitive Visibility - quick glance */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <LineChart className="w-4 h-4 text-indigo-600" />
                <span>Competitive Visibility (quick glance)</span>
              </div>
              <div className="text-xs text-gray-500">
                Traffic: ADS • TR
                {cvCategoryId && bestSellersCategoryOptions.length > 0 && (
                  <> • Cat: {bestSellersCategoryOptions.find(o => o.id === cvCategoryId)?.label || cvCategoryId}</>
                )}
              </div>
            </div>
            <div className="flex items-start gap-6">
              <RelVisBars data={cvCompetitors} />
              <div className="flex-1 overflow-x-auto">
                <table className="min-w-full text-[11px]">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-2 py-1">rank</th>
                      <th className="px-2 py-1">domain</th>
                      <th className="px-2 py-1">relVis %</th>
                      <th className="px-2 py-1">higher %</th>
                      <th className="px-2 py-1">overlap %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cvCompetitors.map((c, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 text-right">{c.rank}</td>
                        <td className="px-2 py-1">{c.domain}{c.isYourDomain ? ' (you)' : ''}</td>
                        <td className="px-2 py-1 text-right">{(c.relativeVisibility * 100).toFixed(1)}%</td>
                        <td className="px-2 py-1 text-right">{(c.higherPositionRate * 100).toFixed(1)}%</td>
                        <td className="px-2 py-1 text-right">{(c.pageOverlapRate * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Best Sellers */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <ShoppingCart className="w-4 h-4 text-indigo-600" />
                <span>Best Sellers (snapshot)</span>
              </div>
              <div className="flex flex-wrap items-center justify-start sm:justify-end gap-2 min-w-0">
                <div className="text-[11px] text-gray-500 whitespace-nowrap">Category</div>
                <select
                  value={bestSellersCategoryId}
                  onChange={(e) => setBestSellersCategoryId(String(e.target.value || '').trim())}
                  className="px-2 py-1 text-[11px] border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 max-w-[360px] w-full sm:w-auto"
                  title="Best Sellers categoryId"
                >
                  {/* Keep current selection even if options are empty */}
                  {bestSellersCategoryOptionsLoading && !bestSellersCategoryOptions.length ? (
                    <option value={bestSellersCategoryId || '166'}>Loading…</option>
                  ) : bestSellersCategoryOptions.length ? (
                    bestSellersCategoryOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))
                  ) : (
                    <option value={bestSellersCategoryId || '166'}>{bestSellersCategoryId || '166'}</option>
                  )}
                </select>
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  TR • Weekly {bestSellersLoading ? '• loading…' : ''}{!bestSellersLoading && bestSellers?.length ? ` • ${bestSellers.length} items` : ''}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-2 py-1">rank</th>
                    <th className="px-2 py-1">title</th>
                    <th className="px-2 py-1">brand</th>
                    <th className="px-2 py-1">category</th>
                    <th className="px-2 py-1">relative</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedBestSellers.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1 text-right">{r.rank}</td>
                      <td className="px-2 py-1">{r.title}</td>
                      <td className="px-2 py-1">{r.brand}</td>
                      <td className="px-2 py-1">{r.category}</td>
                      <td className="px-2 py-1">{r.relativeDemand}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {bestSellersPageCount > 1 && (
              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="text-[11px] text-gray-500">
                  Page {Math.min(bestSellersPage, bestSellersPageCount)} / {bestSellersPageCount}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setBestSellersPage(p => Math.max(1, p - 1))}
                    disabled={bestSellersPage <= 1}
                    className="px-2 py-1 text-[11px] rounded border bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Prev
                  </button>
                  {bestSellersPageButtons.map((p, idx) => {
                    const prev = bestSellersPageButtons[idx - 1];
                    const showDots = idx > 0 && prev != null && p - prev > 1;
                    return (
                      <React.Fragment key={p}>
                        {showDots ? <span className="px-1 text-[11px] text-gray-400">…</span> : null}
                        <button
                          onClick={() => setBestSellersPage(p)}
                          className={`px-2 py-1 text-[11px] rounded border ${p === bestSellersPage ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50'}`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  })}
                  <button
                    onClick={() => setBestSellersPage(p => Math.min(bestSellersPageCount, p + 1))}
                    disabled={bestSellersPage >= bestSellersPageCount}
                    className="px-2 py-1 text-[11px] rounded border bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Topic trends */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <Globe className="w-4 h-4 text-indigo-600" />
                <span>Topic Trends</span>
              </div>
              <div className="text-xs text-gray-500">Last 7 days • TR</div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[11px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-2 py-1">country</th>
                    <th className="px-2 py-1">topic</th>
                    <th className="px-2 py-1">interest</th>
                    <th className="px-2 py-1">last 7</th>
                    <th className="px-2 py-1">last 30</th>
                    <th className="px-2 py-1">last 90</th>
                    <th className="px-2 py-1">last 120</th>
                  </tr>
                </thead>
                <tbody>
                  {topicTrends.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1">{r.customerCountryCode}</td>
                      <td className="px-2 py-1">{r.topic}</td>
                      <td className="px-2 py-1 text-right">{(r.searchInterest * 100).toFixed(1)}%</td>
                      <td className="px-2 py-1 text-right">{(r.last7 * 100).toFixed(1)}%</td>
                      <td className="px-2 py-1 text-right">{(r.last30 * 100).toFixed(1)}%</td>
                      <td className="px-2 py-1 text-right">{(r.last90 * 100).toFixed(1)}%</td>
                      <td className="px-2 py-1 text-right">{(r.last120 * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


